/**
 * JSON-LD Schema Builders for Articles
 * Multi-language support, follows schema.org/Article spec
 */

import { generateBreadcrumbSchema } from "./schemaGenerator";

interface StandaloneArticleOpts {
  url: string;
  headline: string;
  description: string;
  image?: string;
  authorName: string;
  datePublished: string;
  dateModified?: string;
  breadcrumbs: Array<{ name: string; url: string }>;
  organizationName?: string;
  organizationLogo?: string;
}

interface CardArticleOpts extends StandaloneArticleOpts {
  aboutCreditCardId: string; // URL + "#creditcard"
}

interface ArticleListOpts {
  pageUrl: string;
  items: Array<{
    url: string;
    headline: string;
    description?: string;
    image?: string;
    datePublished?: string;
  }>;
}

/**
 * Truncate text with ellipsis
 */
function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.substring(0, maxLen - 3) + "...";
}

/**
 * Strip HTML tags from string
 */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

/**
 * Generates JSON-LD for standalone article (guides, news, etc.)
 */
export function standaloneArticleJsonLd(opts: StandaloneArticleOpts & {
  wordCount?: number;
  keywords?: string[];
}): Record<string, any> {
  const { 
    url, 
    headline, 
    description, 
    image, 
    authorName, 
    datePublished, 
    dateModified, 
    breadcrumbs, 
    organizationName, 
    organizationLogo,
    wordCount,
    keywords
  } = opts;
  
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  const origin = new URL(url).origin;
  
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url,
      "breadcrumb": breadcrumbSchema
    },
    "headline": truncate(headline, 120),
    "description": truncate(stripHtml(description), 160),
    "author": {
      "@type": "Organization",
      "name": organizationName || "Redaktion Kreditkarten.de",
      "url": origin
    },
    "publisher": {
      "@type": "Organization",
      "name": organizationName || "Kreditkarten.de",
      "logo": {
        "@type": "ImageObject",
        "url": organizationLogo || `${origin}/logo.png`
      }
    },
    "datePublished": datePublished,
    "inLanguage": "de",
    "articleSection": "Ratgeber"
  };
  
  // Add optional fields
  if (image) {
    schema.image = [image]; // Array format as per schema.org recommendation
  }
  
  if (dateModified) {
    schema.dateModified = dateModified;
  }
  
  if (wordCount) {
    schema.wordCount = wordCount;
  }
  
  if (keywords && keywords.length > 0) {
    schema.keywords = keywords;
  } else {
    // Default keywords for credit card articles
    schema.keywords = ["Kreditkarte", "Zinsen", "Gebühren", "Finanzen"];
  }
  
  return schema;
}

/**
 * Generates JSON-LD for card-specific article (on card detail page)
 * Links to CreditCard schema via `about: { "@id": ... }`
 */
export function cardArticleJsonLd(opts: CardArticleOpts & { organizationName?: string; organizationLogo?: string }): Record<string, any> {
  const { url, headline, description, image, authorName, datePublished, breadcrumbs, aboutCreditCardId, organizationName, organizationLogo } = opts;
  
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  const origin = new URL(url).origin;
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": truncate(headline, 120),
    "description": truncate(stripHtml(description), 160),
    ...(image && { image }),
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": organizationName || "Kreditkarten Vergleich",
      "logo": {
        "@type": "ImageObject",
        "url": organizationLogo || `${origin}/logo.png`
      }
    },
    "datePublished": datePublished,
    "inLanguage": "de-DE",
    "about": {
      "@id": aboutCreditCardId
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url,
      "breadcrumb": breadcrumbSchema
    }
  };
}

/**
 * Generates JSON-LD for article listing (search results, homepage feed)
 */
export function articleListJsonLd(opts: ArticleListOpts & { listName?: string; listDescription?: string }): Record<string, any> {
  const { pageUrl, items, listName, listDescription } = opts;
  
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName || "Artikel",
    "description": listDescription || "Leitfäden und Neuigkeiten über Kreditkarten",
    "numberOfItems": items.length,
    "itemListElement": items.map((item, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": "Article",
        "@id": item.url,
        "headline": truncate(item.headline, 120),
        "url": item.url,
        ...(item.description && { description: truncate(stripHtml(item.description), 160) }),
        ...(item.image && { image: item.image }),
        ...(item.datePublished && { datePublished: item.datePublished })
      }
    }))
  };
}
